import { useCheckIsCif } from "Containers/CampusMap/useCheckIsCif";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  checkIsDirectionsViewActive,
  checkIsTrailsEnabled,
  checkIsTrailsViewActive,
  getCorrectPlatform,
  getRegFormType,
} from "Utils/utils";
import { IRootState } from "../../../store/rootState";
import { shouldAllowAnalytics } from "../tracking/onetrust";
import gainsight from "./gainsight";

const checkIsRegistrationModuleActivated = (
  modules: Record<string, any>[],
  originalSource: string,
  platformShortName: string
) => {
  const platform = getCorrectPlatform(platformShortName, originalSource);
  const registrationModule = modules?.filter(
    (m) => m.moduleid === "registration"
  )[0];
  return registrationModule && registrationModule?.[platform] === "1";
};

const useGeneralGlobalContext = (isGainsightInitialized) => {
  useEffect(() => {
    if (isGainsightInitialized) {
      gainsight.globalContext("set", {
        product_category: "Aria Viewer",
        virtual_experience_product: "VT",
        virtual_experience_sub_product: "iMap",
        referrer_domain: document.referrer,
        window_domain: window.location?.hostname,
        iMap_using_current_location: false, // this is set to true when GeoLocation button is clicked
      });
    }
  }, [isGainsightInitialized]);
};

const useRegistrationGlobalContext = (isGainsightInitialized) => {
  const { hasRegisteredUser } = useSelector(
    (store: IRootState) => store.analytics.registration
  );
  const { originalSource, platform: platformShortName } = useSelector(
    (store: IRootState) => store.tour
  );
  const { modules } = useSelector((store: any) => store.locationModulesMatrix);
  const isCif = useCheckIsCif(modules, platformShortName, originalSource);

  useEffect(() => {
    if (isGainsightInitialized) {
      const isRegistrationModuleActivated = checkIsRegistrationModuleActivated(
        modules,
        originalSource,
        platformShortName
      );
      const registrationStatus = isRegistrationModuleActivated
        ? hasRegisteredUser
        : "N/A";

      gainsight.globalContext("set", {
        registration_form_type: isRegistrationModuleActivated
          ? getRegFormType(isCif)
          : "Disabled",
        registration_status: registrationStatus,
        registration_status_virtual_exp: registrationStatus,
      });
    }
  }, [
    isGainsightInitialized,
    hasRegisteredUser,
    isCif,
    modules,
    originalSource,
    platformShortName,
  ]);
};

const useUserIdentifierGlobalContext = (isGainsightInitialized) => {
  const userIdentifier = useSelector(
    (store: any) => store.analyticsManager?.userIdentifier
  );
  useEffect(() => {
    if (isGainsightInitialized) {
      // eslint-disable-next-line camelcase
      const { institution_id, institution_name, location_id, location_name } =
        userIdentifier ?? {};
      gainsight.globalContext("set", {
        institution_id,
        institution_name,
        location_id,
        location_name,
      });
    }
  }, [isGainsightInitialized, userIdentifier]);
};

const useMapSwitcherGlobalContext = (isGainsightInitialized) => {
  const currentView = useSelector(
    (store: any) => store.mapSwitcher.currentView
  );
  useEffect(() => {
    if (isGainsightInitialized) {
      gainsight.globalContext("set", {
        iMap_map_view_selected: currentView,
      });
    }
  }, [isGainsightInitialized, currentView]);
};

const useStopsGlobalContext = (isGainsightInitialized: boolean) => {
  const stops = useSelector((store: any) => store.stops);
  useEffect(() => {
    if (isGainsightInitialized) {
      const allPlacesCount = stops.length;
      let destinationsCount = 0;
      let pointOfInterestCount = 0;
      stops.forEach((stop) => {
        if (stop?.isPointOfInterest) {
          pointOfInterestCount += 1;
        } else {
          destinationsCount += 1;
        }
      });

      gainsight.globalContext("set", {
        iMap_num_of_all_places: allPlacesCount,
        iMap_num_of_destinations: destinationsCount,
        iMap_num_of_POI: pointOfInterestCount,
      });
    }
  }, [isGainsightInitialized, stops]);
};

const useTrailsGlobalContext = (isGainsightInitialized: boolean) => {
  const suggestedTrails = useSelector(
    (store: any) => store.trails.suggestedTrails
  );
  useEffect(() => {
    if (isGainsightInitialized) {
      gainsight.globalContext("set", {
        iMap_is_trails_enabled: checkIsTrailsEnabled(suggestedTrails.length),
        iMap_num_of_trails: suggestedTrails.filter(
          (trail) => trail.Stops?.length > 0
        ).length,
      });
    }
  }, [isGainsightInitialized, suggestedTrails]);
};

const useSearchOptionsViewGlobalContext = (isGainsightInitialized: boolean) => {
  const view = useSelector((store: any) => store.searchOptionsView.view);
  useEffect(() => {
    if (isGainsightInitialized) {
      gainsight.globalContext("set", {
        iMap_is_trails_in_use: checkIsTrailsViewActive(view),
        iMap_is_directions_in_use: checkIsDirectionsViewActive(view),
      });
    }
  }, [isGainsightInitialized, view]);
};

export const useGainsightGlobalContext = (isGainsightInitialized: boolean) => {
  useGeneralGlobalContext(isGainsightInitialized);
  useRegistrationGlobalContext(isGainsightInitialized);
  useUserIdentifierGlobalContext(isGainsightInitialized);
  useMapSwitcherGlobalContext(isGainsightInitialized);
  useStopsGlobalContext(isGainsightInitialized);
  useTrailsGlobalContext(isGainsightInitialized);
  useSearchOptionsViewGlobalContext(isGainsightInitialized);
};

export const setGeoLocationGlobalContext = (
  isUsingCurrentLocation: boolean
) => {
  // @ts-ignore
  if (shouldAllowAnalytics(window.OnetrustActiveGroups ?? "")) {
    gainsight.globalContext("set", {
      iMap_using_current_location: isUsingCurrentLocation,
    });
  }
};
try {
    // @ts-ignore
    useGainsightGlobalContext.displayName = "useGainsightGlobalContext";
    // @ts-ignore
    useGainsightGlobalContext.__docgenInfo = { "description": "", "displayName": "useGainsightGlobalContext", "props": { "valueOf": { "defaultValue": null, "description": "Returns the primitive value of the specified object.", "name": "valueOf", "required": false, "type": { "name": "() => boolean" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/analyticsManager/trackers/gainsightGlobalContextUtils.ts#useGainsightGlobalContext"] = { docgenInfo: useGainsightGlobalContext.__docgenInfo, name: "useGainsightGlobalContext", path: "src/shared/analyticsManager/trackers/gainsightGlobalContextUtils.ts#useGainsightGlobalContext" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    setGeoLocationGlobalContext.displayName = "setGeoLocationGlobalContext";
    // @ts-ignore
    setGeoLocationGlobalContext.__docgenInfo = { "description": "", "displayName": "setGeoLocationGlobalContext", "props": { "valueOf": { "defaultValue": null, "description": "Returns the primitive value of the specified object.", "name": "valueOf", "required": false, "type": { "name": "() => boolean" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/analyticsManager/trackers/gainsightGlobalContextUtils.ts#setGeoLocationGlobalContext"] = { docgenInfo: setGeoLocationGlobalContext.__docgenInfo, name: "setGeoLocationGlobalContext", path: "src/shared/analyticsManager/trackers/gainsightGlobalContextUtils.ts#setGeoLocationGlobalContext" };
}
catch (__react_docgen_typescript_loader_error) { }